import { useState } from 'react'
import InquiryModal from './InquiryModal'

interface ServiceCardProps {
  title: string
  description: string
  features: string[]
  available: boolean
  onInquire?: () => void
}

function ServiceCard({ title, description, features, available, onInquire }: ServiceCardProps) {
  return (
    <div
      className="p-8 md:p-10 h-full flex flex-col"
      style={{ backgroundColor: 'var(--color-cream-light)' }}
    >
      {!available && (
        <span
          className="text-xs font-medium tracking-wider uppercase mb-4 inline-block"
          style={{ color: 'var(--color-gold)' }}
        >
          Coming Soon
        </span>
      )}

      <h3
        className="font-heading text-2xl md:text-3xl mb-4"
        style={{ color: 'var(--color-dark)' }}
      >
        {title}
      </h3>

      <p
        className="text-base leading-relaxed mb-6"
        style={{ color: 'var(--color-dark-light)' }}
      >
        {description}
      </p>

      <ul className="space-y-3 mb-8">
        {features.map((feature, index) => (
          <li
            key={index}
            className="flex items-start gap-3 text-sm"
            style={{ color: 'var(--color-dark-light)' }}
          >
            <span style={{ color: 'var(--color-gold)' }}>•</span>
            {feature}
          </li>
        ))}
      </ul>

      {available && onInquire && (
        <div className="mt-auto">
          <button
            onClick={onInquire}
            className="btn-secondary w-full"
          >
            Inquire
          </button>
        </div>
      )}
    </div>
  )
}

interface ServiceData {
  title: string
  description: string
  features: string[]
  available: boolean
  hasInquiry: boolean
}

const services: ServiceData[] = [
  {
    title: 'Group Classes',
    description: 'Join our welcoming community for in-person yoga sessions designed for all levels.',
    features: [
      'All levels welcome',
      'Vinyasa & Hatha styles',
      'Small group settings',
      'Personalized adjustments',
    ],
    available: true,
    hasInquiry: true,
  },
  {
    title: 'Private Sessions',
    description: 'One-on-one instruction tailored to your specific goals and needs.',
    features: [
      'Customized practice',
      'Flexible scheduling',
      'Individual attention',
      'Progress tracking',
    ],
    available: true,
    hasInquiry: true,
  },
  {
    title: 'Coaching',
    description: 'Personalized guidance to help you reconnect with your body, build self-trust, and navigate life with greater ease.',
    features: [
      'Somatic-informed approach',
      'Embodied awareness practices',
      'Nervous system support',
      'Intuitive self-discovery',
    ],
    available: true,
    hasInquiry: true,
  },
  {
    title: 'Workshops & Retreats',
    description: 'Immersive experiences to deepen your practice and connect with yourself.',
    features: [
      'Themed workshops',
      'Weekend retreats',
      'Guest instruction',
      'Wellness integration',
    ],
    available: false,
    hasInquiry: false,
  },
]

export default function Services() {
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedService, setSelectedService] = useState('')

  const handleInquire = (serviceName: string) => {
    setSelectedService(serviceName)
    setModalOpen(true)
  }

  return (
    <>
      <section
        id="services"
        className="section-padding"
        style={{ backgroundColor: 'var(--color-cream)' }}
      >
        <div className="container-custom">
          {/* Section Header */}
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p
              className="text-sm font-medium tracking-[0.2em] uppercase mb-4"
              style={{ color: 'var(--color-gold)' }}
            >
              Offerings
            </p>

            <h2
              className="font-heading text-4xl md:text-5xl mb-6"
              style={{ color: 'var(--color-dark)' }}
            >
              How We Can Practice Together
            </h2>

            <p
              className="text-base md:text-lg leading-relaxed"
              style={{ color: 'var(--color-dark-light)' }}
            >
              Whether you prefer the energy of a group or the focus of individual
              attention, there's a path here for you.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {services.map((service) => (
              <ServiceCard
                key={service.title}
                title={service.title}
                description={service.description}
                features={service.features}
                available={service.available}
                onInquire={service.hasInquiry ? () => handleInquire(service.title) : undefined}
              />
            ))}
          </div>
        </div>
      </section>

      <InquiryModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        serviceName={selectedService}
      />
    </>
  )
}
